import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProfileSection from './components/ProfileSection';
import MemberQRSection from './components/MemberQRSection';
import PointsLedger from './components/PointsLedger';
import NotificationPreferences from './components/NotificationPreferences';
import PaymentMethods from './components/PaymentMethods';
import LocationPreferences from './components/LocationPreferences';
import AccountSettings from './components/AccountSettings';

const MyAccount = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);

  // Mock user data
  const [userData, setUserData] = useState({
    id: 'user_123',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@email.com',
    phone: '+1 (555) 123-4567',
    avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face'
  });

  // Mock membership data
  const [membershipData, setMembershipData] = useState({
    memberNumber: 'TIX789012',
    joinDate: 'Jan 2022',
    tier: 'Gold',
    multiplier: '2',
    points: 3450
  });

  // Mock points data
  const [pointsData, setPointsData] = useState({
    balance: 3450,
    lifetimeEarned: 8920,
    availableRewards: [
      {
        id: 'reward_1',
        title: '$5 Event Credit',
        description: 'Get $5 off your next ticket purchase',
        cost: 500
      },
      {
        id: 'reward_2',
        title: '$10 Event Credit',
        description: 'Get $10 off your next ticket purchase',
        cost: 1000
      },
      {
        id: 'reward_3',
        title: 'VIP Upgrade',
        description: 'Free VIP upgrade on eligible events',
        cost: 2500
      },
      {
        id: 'reward_4',
        title: 'Free Merchandise',
        description: 'Get a free event t-shirt or poster',
        cost: 1500
      }
    ],
    transactions: [
      {
        id: 'txn_1',
        type: 'earned',
        description: 'Concert ticket purchase - Madison Square Garden',
        points: 150,
        date: '2024-09-25T10:30:00Z'
      },
      {
        id: 'txn_2',
        type: 'redeemed',
        description: 'Redeemed $5 Event Credit',
        points: 500,
        date: '2024-09-20T14:15:00Z'
      },
      {
        id: 'txn_3',
        type: 'earned',
        description: 'Festival pass purchase - Central Park',
        points: 300,
        date: '2024-09-18T09:45:00Z'
      },
      {
        id: 'txn_4',
        type: 'bonus',
        description: 'Birthday bonus points',
        points: 100,
        date: '2024-09-15T00:00:00Z'
      },
      {
        id: 'txn_5',
        type: 'earned',
        description: 'Theater show purchase - Broadway',
        points: 200,
        date: '2024-09-10T16:20:00Z'
      }
    ]
  });

  // Mock notification preferences
  const [notificationPreferences, setNotificationPreferences] = useState({
    events: {
      newEvents: { email: true, sms: false, push: true },
      eventUpdates: { email: true, sms: true, push: true },
      eventReminders: { email: true, sms: true, push: true }
    },
    orders: {
      orderConfirmation: { email: true, sms: true, push: true },
      ticketDelivery: { email: true, sms: false, push: true },
      refundUpdates: { email: true, sms: true, push: true }
    },
    marketing: {
      promotions: { email: true, sms: false, push: false },
      newsletter: { email: true, sms: false, push: false },
      recommendations: { email: true, sms: false, push: true }
    },
    account: {
      securityAlerts: { email: true, sms: true, push: true },
      accountUpdates: { email: true, sms: false, push: true },
      pointsUpdates: { email: true, sms: false, push: true }
    }
  });

  // Mock payment methods
  const [paymentMethods, setPaymentMethods] = useState([
    {
      id: 'pm_1',
      brand: 'Visa',
      last4: '4242',
      expiryMonth: '12',
      expiryYear: '2025',
      isDefault: true
    },
    {
      id: 'pm_2',
      brand: 'Mastercard',
      last4: '8888',
      expiryMonth: '08',
      expiryYear: '2026',
      isDefault: false
    }
  ]);

  // Mock location data
  const [locationData, setLocationData] = useState({
    primaryLocation: 'New York, NY',
    searchRadius: 25,
    autoDetectLocation: true,
    favoriteVenues: [
      {
        id: 'venue_1',
        name: 'Madison Square Garden',
        location: 'New York, NY'
      },
      {
        id: 'venue_2',
        name: 'Brooklyn Bowl',
        location: 'Brooklyn, NY'
      }
    ]
  });

  const tabs = [
    { id: 'profile', label: 'Profile', icon: 'User' },
    { id: 'membership', label: 'Membership', icon: 'CreditCard' },
    { id: 'points', label: 'Points', icon: 'Star' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'payments', label: 'Payments', icon: 'CreditCard' },
    { id: 'location', label: 'Location', icon: 'MapPin' },
    { id: 'settings', label: 'Settings', icon: 'Settings' }
  ];

  // Handler functions
  const handleUpdateProfile = async (profileData) => {
    setIsLoading(true);
    // Mock API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setUserData(prev => ({ ...prev, ...profileData }));
    setIsLoading(false);
  };

  const handleRedeemReward = async (reward) => {
    setIsLoading(true);
    // Mock API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const newTransaction = {
      id: `txn_${Date.now()}`,
      type: 'redeemed',
      description: `Redeemed ${reward?.title}`,
      points: reward?.cost,
      date: new Date()?.toISOString()
    };

    setPointsData(prev => ({
      ...prev,
      balance: prev?.balance - reward?.cost,
      transactions: [newTransaction, ...prev?.transactions]
    }));
    
    setIsLoading(false);
    alert(`Successfully redeemed ${reward?.title}!`);
  };

  const handleUpdateNotifications = async (preferences) => {
    setIsLoading(true);
    // Mock API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setNotificationPreferences(preferences);
    setIsLoading(false);
  };

  const handleAddPaymentMethod = async (paymentData) => {
    setIsLoading(true);
    // Mock API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const newPaymentMethod = {
      id: `pm_${Date.now()}`,
      brand: 'Visa', // Mock detection
      last4: paymentData?.cardNumber?.slice(-4),
      expiryMonth: paymentData?.expiryDate?.split('/')?.[0],
      expiryYear: `20${paymentData?.expiryDate?.split('/')?.[1]}`,
      isDefault: paymentMethods?.length === 0
    };

    setPaymentMethods(prev => [...prev, newPaymentMethod]);
    setIsLoading(false);
  };

  const handleRemovePaymentMethod = async (paymentId) => {
    setIsLoading(true);
    // Mock API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setPaymentMethods(prev => prev?.filter(pm => pm?.id !== paymentId));
    setIsLoading(false);
  };

  const handleSetDefaultPayment = async (paymentId) => {
    setIsLoading(true);
    // Mock API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setPaymentMethods(prev => 
      prev?.map(pm => ({ ...pm, isDefault: pm?.id === paymentId }))
    );
    setIsLoading(false);
  };

  const handleUpdateLocation = async (locationPrefs) => {
    setIsLoading(true);
    // Mock API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLocationData(locationPrefs);
    setIsLoading(false);
  };

  const handleExportData = async () => {
    // Mock data export
    await new Promise(resolve => setTimeout(resolve, 2000));
  };

  const handleDeleteAccount = async () => {
    // Mock account deletion
    await new Promise(resolve => setTimeout(resolve, 2000));
    navigate('/login');
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <ProfileSection
            user={userData}
            onUpdateProfile={handleUpdateProfile}
          />
        );
      case 'membership':
        return (
          <MemberQRSection
            user={userData}
            membershipData={membershipData}
          />
        );
      case 'points':
        return (
          <PointsLedger
            pointsData={pointsData}
            onRedeemReward={handleRedeemReward}
          />
        );
      case 'notifications':
        return (
          <NotificationPreferences
            preferences={notificationPreferences}
            onUpdatePreferences={handleUpdateNotifications}
          />
        );
      case 'payments':
        return (
          <PaymentMethods
            paymentMethods={paymentMethods}
            onAddPaymentMethod={handleAddPaymentMethod}
            onRemovePaymentMethod={handleRemovePaymentMethod}
            onSetDefault={handleSetDefaultPayment}
          />
        );
      case 'location':
        return (
          <LocationPreferences
            locationData={locationData}
            onUpdateLocation={handleUpdateLocation}
          />
        );
      case 'settings':
        return (
          <AccountSettings
            onExportData={handleExportData}
            onDeleteAccount={handleDeleteAccount}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-card border-b border-border shadow-elevation-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                iconName="ArrowLeft"
                onClick={() => navigate('/home')}
                className="text-muted-foreground hover:text-foreground"
              />
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="User" size={20} color="white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-foreground">My Account</h1>
                  <p className="text-sm text-muted-foreground">Manage your profile and preferences</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                iconName="Ticket"
                onClick={() => navigate('/my-tickets')}
              >
                My Tickets
              </Button>
            </div>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-4 lg:gap-8">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <nav className="sticky top-8 space-y-1">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Mobile Tab Navigation */}
          <div className="lg:hidden mb-6">
            <div className="flex overflow-x-auto space-x-1 pb-2">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex-shrink-0 flex items-center space-x-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === tab?.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAccount;